import { Stack } from "@mui/material";
import { useLocation } from "@reach/router";
import { readEnv } from "@repo/helpers";
import {
	DesktopBodyWrapper,
	HeadingXL,
	TranslationKeys,
	useIsDesktop,
	useTypedTranslation,
} from "@repo/ui";
import { isFuture, parseISO } from "date-fns";
import { type FC, type PropsWithChildren } from "react";
import { useCookieConsents } from "../../context/CookieContext";
import { ContactsImage } from "../../pages/contacts";
import CookieDrawer from "../Drawer/CookieDrawer";
import { InfoBannersWrapper } from "../InfoBanners";


export type LayoutBodyProps = PropsWithChildren<{
	pageProps: {
		name: string;
		verticalCenter?: boolean;
		appBar?: string;
	};
}>

const getTitle = (
	page: string,
	t: (key: TranslationKeys, options?: Record<string, unknown>) => string,
): string | undefined => {
	if (!page) {
		return;
	}
	const profileV2Enabled = readEnv("GATSBY_FEATURE_PROFILE_V2_ENABLED") === "true";
	const isProfilePage = page.startsWith("profile");
	if (isProfilePage && profileV2Enabled) {
		return;
	}

	if (isProfilePage) {
		return t("bottomNavigationBar.profile");
	}

	if (page === "contacts") {
		return t("bottomNavigationBar.contacts");
	}
};

const getBackgroundImage = (page: string) => {
	if (page === "contacts") {
		return ContactsImage;
	}
};

export const LayoutBody: FC<LayoutBodyProps> = ({ pageProps, children }) => {
	const isDesktop = useIsDesktop();
	const { t } = useTypedTranslation();
	const { data: cookieData, displayCookieForm } = useCookieConsents();
	const cookieHasExpiered = cookieData.expires ? !isFuture(parseISO(cookieData.expires)) : false;
	const title = getTitle(pageProps.name, t);
	const BackgroundImage: any = getBackgroundImage(pageProps.name);

	const location = useLocation();

	const desktopContent = (
		<>
			{title && <HeadingXL>{title}</HeadingXL>}
			{pageProps.verticalCenter ? (
				<Stack sx={{ justifyContent: "center" }}>{children}</Stack>
			) : (
				children
			)}
		</>
	);

	return (
		<Stack
			id="layout-body-container"
			sx={{
				width: "100%",
				p: { md: 7, xs: 4 },
			}}
		>
			{isDesktop ? (
				<>
					{BackgroundImage ? (
						<DesktopBodyWrapper
							verticalCenter={pageProps.verticalCenter}
							BackgroundImage={BackgroundImage}
							InfoBanners={() => <InfoBannersWrapper pageName={pageProps.name} showCoreSystemDownWarning />}>
							<BackgroundImage>{desktopContent}</BackgroundImage>
						</DesktopBodyWrapper>
					) : (
						<DesktopBodyWrapper
							verticalCenter={pageProps.verticalCenter}
							BackgroundImage={BackgroundImage}
							InfoBanners={() => <InfoBannersWrapper pageName={pageProps.name} showCoreSystemDownWarning />}>
							{desktopContent}
						</DesktopBodyWrapper>
					)}
				</>
			) : (
				<Stack gap={2}>
					{pageProps.name === "contracts" && (
						<InfoBannersWrapper
							pageName={pageProps.name}
							showCoreSystemDownWarning={false}
						/>
					)}
					{children}
				</Stack>
			)}
			{(!cookieData.consentSet || displayCookieForm || cookieHasExpiered) &&
				location.pathname !== "/penzijni-kalkulacka/" && <CookieDrawer floating={true} />}
		</Stack>
	);
};

